import React, {
  useState,
  useEffect
} from 'react';

import {
  Flex,
  Label,
  Panel,
  Button,
  Paragraph,
  TextInput,
  useNotification,
  LoadingContainer
} from '@sopranodesign/pulse-components';
import { TemplatingTextArea } from '@sopranodesign/pulse-templating';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@sopranodesign/pulse-hooks';

import {
  fetchPrompt,
  updatePrompt
} from '../api';

const Prompt = () => {
  const { hasPermission } = useAuth();
  const canEditPrompt = hasPermission('edit generative ai prompt');

  const { showSuccess, showError } = useNotification();

  const queryClient = useQueryClient();

  const [promptName, setPromptName] = useState('');
  const [promptText, setPromptText] = useState('');

  const {
    isLoading,
    data: promptResponse
  } = useQuery({
    queryKey: ['generative-ai/prompt'],
    queryFn: fetchPrompt
  });

  const {
    isPending,
    mutateAsync
  } = useMutation({
    mutationFn: updatePrompt
  });

  useEffect(() => {
    if (promptResponse?.data && !isLoading) {
      const { name = '', custom_prompt = '' } = promptResponse.data;
      setPromptName(name);
      setPromptText(custom_prompt);
    }
  }, [isLoading, promptResponse]);

  const handleSave = async (event) => {
    event.preventDefault();

    try {
      await mutateAsync({ name: promptName, text: promptText });
      queryClient.invalidateQueries({ queryKey: ['generative-ai/prompt'] });
      showSuccess(`Prompt updated successfully!`);
    } catch (error) {
      showError(`Failed to update prompt: ${error.message}`);
    }
  };

  const isReadOnly = !canEditPrompt;
  const isBusy = isLoading || isPending;

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading prompt...</Paragraph>
      </Flex>
    );
  }

  return (
    <PermissionFilter can="view generative ai prompt">
      <Panel>
        <Flex col mt>
          <Label htmlFor="promptName">Prompt name</Label>
          <TextInput
            id="promptName"
            value={promptName}
            onChange={(e) => setPromptName(e.target.value)}
            readOnly={isReadOnly}
          />
        </Flex>

        <Flex col mt>
          <Label htmlFor="promptText">Prompt text</Label>
          <TemplatingTextArea
            id="promptText"
            value={promptText}
            onChange={setPromptText}
            readOnly={isReadOnly}
          />
        </Flex>

        <PermissionFilter can="edit generative ai prompt">
          <Flex right mt>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isBusy}
            >
              Save
            </Button>
          </Flex>
        </PermissionFilter>
      </Panel>
    </PermissionFilter>
  );
};

export default Prompt;